<template>
  <div
    class="documents-page-wrapper"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div v-if="isDragging" class="drop-zone-overlay">
      <div class="drop-zone-message">
        <UploadCloudIcon/>
        <p>Drop file here to upload to documents</p>
      </div>
    </div>
    <div class="documents-header">
      <div
        v-if="documents.length"
        class="documents-header__left"
        @click="handleHeaderToggle"
        style="cursor: pointer"
      >
        <v-checkbox
          :input-value="false"
          :indeterminate="isAnySelected"
          color="#008C89"
          hide-details
          class="ma-0 pa-0"
          readonly
        />
        <span v-if="isAnySelected" class="documents-header__count">
          {{ selectedDocs.length }} selected:
        </span>
        <span v-else class="documents-header__title">All documents</span>
      </div>

      <div class="documents-header__right">
        <div v-if="selectedDocs.length > 0" class="documents-actions">
          <div class="icon-delete-wrapper" @click="handleDelete">
            <DeleteIcon class="icon-delete"/>
          </div>
          <Button height="36" @click="downloadFile">
            <DownloadIcon/>
            Download {{ selectedDocs.length }}
          </Button>
          <Button height="36" @click="print">
            <PrintIcon/>
            Print {{ selectedDocs.length }}
          </Button>
          <Button v-if="hasPatientEmail" height="36" @click="sendToPatientEmail">
            <EmailIcon/>
            Send to Patient {{ selectedDocs.length }}
          </Button>
        </div>
        <input
          type="file"
          ref="newDocumentInput"
          class="hidden-input"
          accept=".pdf,.png,.jpg,.jpeg"
          @change="onNewDocumentFileSelect"
        >
        <Button height="36"
                :styles="{
                  border: '1px solid #008C89 !important',
                  color: '#008C89'}"
                @click="$refs.newDocumentInput.click()"
        >
          <PlusIcon/>
          New Document
        </Button>
      </div>
    </div>

    <div class="documents-manager">
      <multipane
        v-if="isPreviewPersistent"
        class="custom-resizer"
        layout="vertical"
        @paneResize="onResize"
      >
        <div class="documents-list-container pane"
             :style="{ width: leftPaneWidth + 'px', maxWidth: '600px', minWidth: '286px' }">
          <div v-if="!documents.length" class="no-data-text">
            No Documents Found
          </div>
          <div v-else class="documents-list">
            <div
              v-for="doc in paginatedDocuments"
              :key="doc.id"
              class="document-item"
              :class="{
              'document-item--selected': isSelected(doc.id),
              'document-item--active': activeDocId === doc.id
            }"
              @click="handleDocumentClick(doc)"
            >
              <div class="document-item__checkbox">
                <v-checkbox
                  :input-value="isSelected(doc.id)"
                  @change="toggleDoc(doc.id)"
                  color="#008C89"
                  hide-details
                  class="ma-0 pa-0"
                />
              </div>

              <div class="document-item__main">
                <span class="document-item__name" :title="doc.fileName">{{ doc.fileName }}</span>
                <div class="document-item__info">
                  <div class="badge">
                    <span v-if="doc.hasImportSession">Lab report</span>
                    <span v-else>Document</span>
                  </div>
                  <span class="source">{{ doc.importSourceDataName }}</span>
                </div>
              </div>

              <div class="document-item__meta">
                <div class="document-item__date">
                  {{ $moment(doc.createdOn).format('MM/DD/YYYY, hh:mm A') }}
                </div>
<!--                <div class="document-item__status">-->
<!--                  <SuccessIcon-->
<!--                    v-if="doc.importSessionStatus === 'InProgress' || doc.importSessionStatus === 'Approved'"/>-->
<!--                  <template v-else-if="doc.importSessionStatus === 'Rejected' || doc.importSessionStatus === 'Error'">-->
<!--                    <v-tooltip top :max-width="400">-->
<!--                      <template v-slot:activator="{ on, attrs }">-->
<!--                            <span v-on="on" v-bind="attrs" style="width: 20px; height: 20px; display: flex">-->
<!--                              <ErrorIcon class="icon-error"/>-->
<!--                            </span>-->
<!--                      </template>-->
<!--                      <span>{{ doc.errorMessage || 'Something went wrong' }}</span>-->
<!--                    </v-tooltip>-->
<!--                  </template>-->
<!--                </div>-->
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination-wrapper">
            <v-pagination
              v-model="page"
              :length="totalPages"
              :total-visible="7"
              color="#008C89"
              class="custom-pagination"
            ></v-pagination>
          </div>
        </div>

        <multipane-resizer>
          <div class="center-line"></div>
        </multipane-resizer>

        <div class="session-detail-preview pane" style="flex-grow: 1">
          <div class="session-detail-preview__wrapper" v-if="activeDocId && !loading">
            <div class="detail-preview-bar">
              <div class="bar-col-name">
                <span>{{ activeDoc.fileName }}</span>
              </div>
              <div class="view-switcher" v-if="activeDoc.hasImportSession">
                <button
                  :class="['view-switcher__btn', { 'is-active': viewMode === 'report' }]"
                  @click="viewMode = 'report'"
                >
                  Lab report
                </button>
                <button
                  :class="['view-switcher__btn', { 'is-active': viewMode === 'source' }]"
                  @click="handleSourceViewClick"
                >
                  Source
                </button>
              </div>
            </div>
            <div class="detail-content">
              <BloodImportReportPreview
                v-if="viewMode === 'report' && activeDoc.hasImportSession"
                :reportInfo="reportInfo"
                :patient="patient"
                :markersData="localMarkers"
                :headers="headers"
                :expandedRows="expandedRows"
                @toggle-group="toggleRow"
              />
              <div v-if="viewMode === 'source'" class="pdf-viewer-container">
                <div class="pdf-canvas-render">
                  <vue-pdf-embed
                    :key="previewFileUrl"
                    :source="previewFileUrl"
                    class="pdf-render-surface"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="!activeDocId" class="empty-placeholder-card">
            <div class="placeholder-table-header">
              <div v-for="header in headers" :key="header.text" :style="{ width: header.width }">
                {{ header.text }}
              </div>
            </div>
            <div class="placeholder-content">
              <span>Select import data to see blood result</span>
            </div>
          </div>
        </div>
      </multipane>


      <div v-else class="documents-list-container">
        <div v-if="!documents.length" class="no-data-text">
          No Documents Found
        </div>
        <div v-else class="documents-list">
          <div
            v-for="doc in paginatedDocuments"
            :key="doc.id"
            class="document-item"
            :class="{
              'document-item--selected': isSelected(doc.id),
              'document-item--active': activeDocId === doc.id
            }"
            @click="handleDocumentClick(doc)"
          >
            <div class="document-item__checkbox">
              <v-checkbox
                :input-value="isSelected(doc.id)"
                @change="toggleDoc(doc.id)"
                color="#008C89"
                hide-details
                class="ma-0 pa-0"
              />
            </div>

            <div class="document-item__main">
              <span class="document-item__name" :title="doc.fileName">{{ doc.fileName }}</span>
              <div class="document-item__info">
                <div class="badge">
                  <span v-if="doc.hasImportSession">Lab report</span>
                  <span v-else>Document</span>
                </div>
                <span class="source">{{ doc.importSourceDataName }}</span>
              </div>
            </div>

            <div class="document-item__meta">
              <div class="document-item__date">
                {{ $moment(doc.createdOn).format('MM/DD/YYYY, hh:mm A') }}
              </div>
<!--              <div class="document-item__status">-->
<!--                <SuccessIcon-->
<!--                  v-if="doc.importSessionStatus === 'InProgress' || doc.importSessionStatus === 'Approved'"/>-->
<!--                <template v-else-if="doc.importSessionStatus === 'Rejected' || doc.importSessionStatus === 'Error'">-->
<!--                  <v-tooltip top :max-width="400">-->
<!--                    <template v-slot:activator="{ on, attrs }">-->
<!--                            <span v-on="on" v-bind="attrs" style="width: 20px; height: 20px; display: flex">-->
<!--                              <ErrorIcon class="icon-error"/>-->
<!--                            </span>-->
<!--                    </template>-->
<!--                    <span>{{ doc.errorMessage || 'Something went wrong' }}</span>-->
<!--                  </v-tooltip>-->
<!--                </template>-->
<!--              </div>-->
            </div>
          </div>
        </div>
        <div v-if="totalPages > 1" class="pagination-wrapper">
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="7"
            color="#008C89"
            class="custom-pagination"
          ></v-pagination>
        </div>
      </div>
    </div>

    <div v-if="showMainLoader" class="loading d-flex justify-center px-2 py-2 align-center">
      <v-progress-circular
        indeterminate
        color="#008C89"
      ></v-progress-circular>
    </div>

    <div class="custom-alert">
      <v-alert :color="!isErrorMessage ? '#7ac1fb' : 'rgb(207, 70, 85)'" dismissible type="info" v-model="alertIsOpen">
        <span style="font-size: 14px">{{ popupMessage }}</span>
      </v-alert>
    </div>

    <ModalDocumentPreview
      v-if="dialogs.modalDocumentPreview.isOpen"
      :loading="actionLoading"
      :hasPatientEmail="hasPatientEmail"
      :patient="patient"
      @download="downloadFile"
      @print="print"
      @send-email="sendToPatientEmail"
      @load-source="loadSourceFile(activeDocId)"
      @delete="handleDeleteFromModal"
    />
  </div>
</template>

<script>
import {mapMutations, mapState, mapActions} from 'vuex'
import Button from '@/components/elements/Button'
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'
import Mapping from '@/mixins/Mapping'
import BloodImportReportPreview from '@/components/layouts/blood/BloodImportReportPreview'
import {BLOOD_MARKERS_HEADERS} from '@/constants/tableHeaders'
import ModalDocumentPreview from '@/components/layouts/dialogs/modal-document-preview'

import DownloadIcon from '@/assets/icons/components/download-green.svg'
import PrintIcon from '@/assets/icons/components/print-green.svg'
import EmailIcon from '@/assets/icons/components/email-green.svg'
import PlusIcon from '@/assets/icons/components/plus-sm-green.svg'
import FileIcon from '@/assets/icons/components/file.svg'
import SuccessIcon from '@/assets/icons/components/success-icon-green.svg'
import ErrorIcon from '@/assets/icons/components/error-icon-red.svg'
import UploadCloudIcon from '@/assets/icons/components/upload-cloud.svg'
import DeleteIcon from '@/assets/icons/components/delete-icon.svg'

const STORAGE_KEY = 'documents_left_pane_width'

export default {
  name: 'DocumentsManager',
  mixins: [Mapping],
  components: {
    VuePdfEmbed,
    Button,
    BloodImportReportPreview,
    ModalDocumentPreview,
    DownloadIcon, PrintIcon, EmailIcon, PlusIcon,
    FileIcon, SuccessIcon, ErrorIcon, UploadCloudIcon,
    DeleteIcon
  },
  data: () => ({
    leftPaneWidth: 422,
    loading: false,
    actionLoading: false,
    popupMessage: '',
    alertIsOpen: false,
    isErrorMessage: false,
    isDragging: false,
    localSession: [],
    localMarkers: [],
    selectedDocs: [],
    activeDocId: null,
    windowWidth: window.innerWidth,
    expandedRows: [],
    viewMode: 'report',
    activeDoc: null,
    previewFileUrl: null,
    page: 1,
    pageSize: 10,
    headers: BLOOD_MARKERS_HEADERS
  }),
  async mounted() {
    await this.fetchDocs()
    window.addEventListener('resize', this.onResizeWindow)

    const savedWidth = localStorage.getItem(STORAGE_KEY)
    if (savedWidth) {
      this.leftPaneWidth = parseInt(savedWidth, 10) || 574
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResizeWindow)
  },
  computed: {
    ...mapState('patients', {
      documents: state => state.patientDocuments || [],
      patient: state => state.patient
    }),
    dialogs() {
      return this.$store.state.dialogs
    },
    isPreviewPersistent() {
      return this.windowWidth > 1234
    },
    reportInfo() {
      const s = this.localSession
      const d = this.activeDoc

      if (!d) return {}

      const nameParts = [s.patientFirstName, s.patientLastName].filter(Boolean)
      const fullName = nameParts.length > 0 ? nameParts.join(' ') : '-'

      return {
        fileName: s?.fileName || d.fileName,
        source: s?.ImportSourceDataName || d.importSourceDataName || 'Doctor',
        patientName: fullName,
        patientDob: s.patientDateOfBirth ? this.$moment(s.patientDateOfBirth).format('MM/DD/YYYY') : '-',
        dateCollected: s?.dateCollected ? this.$moment(s.dateCollected).format('MM/DD/YYYY') : '-',
        dateReceived: s?.dateReceived ? this.$moment(s.dateReceived).format('MM/DD/YYYY') : '-',
        createdOn: d.createdOn ? this.$moment(d.createdOn).format('MM/DD/YYYY, hh:mm A') : '-',
        labName: s?.labName || 'Unknown Lab',
        fasting: s?.isFasting ? 'Yes' : 'No',
        valueCount: s?.valueCount || d.valueCount || 0
      }
    },
    isAnySelected() {
      return this.selectedDocs.length > 0
    },
    showMainLoader() {
      return (this.loading || this.actionLoading) && !this.dialogs.modalDocumentPreview.isOpen
    },
    paginatedDocuments() {
      const start = (this.page - 1) * this.pageSize
      const end = start + this.pageSize
      return this.documents.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.documents.length / this.pageSize)
    },
    hasPatientEmail() {
      return !!(this.patient && this.patient.email)
    }
  },

  methods: {
    ...mapActions('patients', ['getPatient', 'getPatientDocuments']),
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    ...mapActions('documents', [
      'uploadPatientDocument',
      'downloadDocumentFile',
      'sendDocumentsToPatient',
      'downloadDocumentsZip',
      'removePatientDocument'
    ]),
    ...mapActions('blood', ['fetchSessionDetail']),
    onResize(pane, container, size) {
      this.leftPaneWidth = size
      this.debouncedSaveWidth(size)
    },

    onResizeWindow() {
      this.windowWidth = window.innerWidth
    },

    debouncedSaveWidth: (() => {
      let timeoutId
      return function (width) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          localStorage.setItem(STORAGE_KEY, width)
        }, 300)
      }
    })(),

    async fetchDocs() {
      this.loading = true
      const patientId = this.$route.params.id

      if (patientId) {
        await this.getPatientDocuments(patientId)
      }
      this.loading = false
    },
    handleHeaderToggle() {
      if (this.isAnySelected) {
        this.selectedDocs = []
      } else {
        this.selectedDocs = this.documents.map(doc => doc.id)
      }
    },
    isSelected(id) {
      return this.selectedDocs.includes(id)
    },
    toggleDoc(id) {
      const index = this.selectedDocs.indexOf(id)
      if (index > -1) {
        this.selectedDocs.splice(index, 1)
      } else {
        this.selectedDocs.push(id)
      }
    },
    toggleRow(name) {
      const index = this.expandedRows.indexOf(name)
      index > -1 ? this.expandedRows.splice(index, 1) : this.expandedRows.push(name)
    },
    async handleDocumentClick(doc) {
      if (this.activeDocId === doc.id) return

      this.activeDocId = doc.id
      this.activeDoc = doc
      this.viewMode = doc.hasImportSession ? 'report' : 'source'

      this.cleanupPreviewUrl()
      this.loading = true

      if (doc.hasImportSession) {
        const res = await this.fetchSessionDetail(doc.importSessionId)

        if (!res) {
          this.loading = false
          return
        }

        this.localSession = res.session
        this.localMarkers = res.groups
        this.expandedRows = res.groups.map(g => g.name)

        this.loading = false
      } else {
        await this.loadSourceFile(doc.id)
      }

      if (!this.isPreviewPersistent) {
        this.TOGGLE_DIALOG({
          name: 'modalDocumentPreview',
          isOpen: true,
          title: doc.fileName,
          data: {
            importSession: this.localSession,
            stagedMarkersItems: this.localMarkers,
            fileUrl: this.previewFileUrl,
            viewMode: this.viewMode,
            hasImportSession: doc.hasImportSession
          }
        })
      }

      this.loading = false
    },
    cleanupPreviewUrl() {
      if (this.previewFileUrl) {
        URL.revokeObjectURL(this.previewFileUrl)
        this.previewFileUrl = null
      }
    },
    async loadSourceFile(id) {
      this.loading = true
      try {
        const blobData = await this.downloadDocumentFile({id})

        if (blobData) {
          this.previewFileUrl = URL.createObjectURL(new Blob([blobData]))

          if (this.dialogs.modalDocumentPreview.isOpen) {
            this.TOGGLE_DIALOG({
              name: 'modalDocumentPreview',
              isOpen: true,
              title: this.activeDoc.fileName,
              data: {
                ...this.dialogs.modalDocumentPreview.data,
                fileUrl: this.previewFileUrl
              }
            })
          }
        }
      } catch (e) {
        console.error('Error loading source file:', e)
      } finally {
        this.loading = false
      }
    },
    async handleSourceViewClick() {
      this.viewMode = 'source'

      if (!this.previewFileUrl) {
        await this.loadSourceFile(this.activeDocId)
      }
    },
    async onNewDocumentFileSelect(e) {
      const file = e.target.files[0]
      if (file) {
        await this.handleFileUpload(file)
      }
      e.target.value = ''
    },
    onDragOver() {
      this.isDragging = true
    },
    onDragLeave() {
      this.isDragging = false
    },
    async onDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file) {
        await this.handleFileUpload(file)
      }
    },
    async handleFileUpload(file) {
      if (!file) return

      this.loading = true
      const patientId = this.$route.params.id

      if (patientId) {
        const res = await this.uploadPatientDocument({file, patientId})

        if (res && res.success) {
          this.page = 1
          await this.fetchDocs()

          this.$emit('refresh-patient')
        }
      }
      this.loading = false
    },
    getTargetIds() {
      if (this.selectedDocs.length > 0) return this.selectedDocs
      if (this.activeDocId) return [this.activeDocId]
      return []
    },
    async handleDelete() {
      const ids = this.getTargetIds()
      if (!ids.length) return

      await this.TOGGLE_DIALOG({
        name: 'confirm',
        isOpen: true,
        title: 'Confirm Deletion',
        description: `Are you sure you want to delete ${ids.length} selected document(s)?`,
        callback: async () => {
          this.TOGGLE_DIALOG({name: 'confirm', isOpen: false})
          this.actionLoading = true

          try {
            const results = await Promise.all(
              ids.map(id => this.removePatientDocument(id))
            )

            const successCount = results.filter(res => res && res.success).length

            if (successCount === ids.length) {
              this.isErrorMessage = false
              this.popupMessage = 'Documents deleted successfully'
            } else {
              this.isErrorMessage = true
              this.popupMessage = `Deleted ${successCount} out of ${ids.length} documents. Some errors occurred.`
            }
            this.page = 1
            this.selectedDocs = []
            this.activeDocId = null
            this.activeDoc = null
            this.cleanupPreviewUrl()
            await this.fetchDocs()
            this.$emit('refresh-patient')
          } catch (e) {
            this.isErrorMessage = true
            this.popupMessage = 'A server error occurred while deleting documents.'
          } finally {
            this.actionLoading = false
            this.alertIsOpen = true
          }
        }
      });
    },
    async downloadFile() {
      const ids = this.getTargetIds()
      const patientId = this.$route.params.id

      if (!ids.length) return

      if (ids.length > 10) {
        this.isErrorMessage = true
        this.alertIsOpen = true
        this.popupMessage = 'At most 10 documents can be processed in a single batch.'
        return
      }

      this.actionLoading = true
      try {
        let blobData = null
        let fileName = 'document.pdf'

        if (ids.length === 1) {
          const doc = this.documents.find(d => d.id === ids[0])
          fileName = doc ? doc.fileName : 'document.pdf'
          blobData = await this.downloadDocumentFile({id: ids[0]})
        } else {
          fileName = `patient_${patientId}_documents.zip`
          blobData = await this.downloadDocumentsZip({patientId, documentIds: ids})
        }

        if (blobData) {
          this.triggerBrowserDownload(blobData, fileName)
        } else {
          this.isErrorMessage = true
          this.alertIsOpen = true
          this.popupMessage = 'Failed to prepare download. Please try again.'
        }
      } catch (e) {
        console.error('Download process failed', e)
      } finally {
        this.actionLoading = false
      }
    },
    triggerBrowserDownload(blob, fileName) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    async print() {
      const ids = this.getTargetIds()
      if (!ids.length) return

      this.actionLoading = true
      try {
        for (const id of ids) {
          const blobData = await this.downloadDocumentFile({id})

          if (blobData) {
            await new Promise((resolve) => {
              const url = window.URL.createObjectURL(new Blob([blobData], {type: 'application/pdf'}))
              const iframe = document.createElement('iframe')

              Object.assign(iframe.style, {
                position: 'absolute',
                top: '-9999px',
                left: '-9999px',
                width: '100%',
                height: '100%',
                border: '0',
                opacity: '0',
              })

              iframe.src = url
              document.body.appendChild(iframe)

              iframe.onload = () => {
                iframe.contentWindow.focus()
                setTimeout(() => {
                  iframe.contentWindow?.print()
                }, 200)

                const cleanUp = () => {
                  if (iframe.parentNode) {
                    document.body.removeChild(iframe)
                  }
                  window.URL.revokeObjectURL(url)
                }
                setTimeout(cleanUp, 60000)

                const finishWaiting = () => {
                  window.removeEventListener('focus', finishWaiting)
                  window.removeEventListener('afterprint', finishWaiting)
                  resolve()
                }

                window.addEventListener('afterprint', finishWaiting)
                window.addEventListener('focus', finishWaiting)
                iframe.contentWindow.addEventListener('afterprint', finishWaiting)

                setTimeout(finishWaiting, 5000)
              }
            })
          }
        }
      } catch (e) {
        console.error('Print error:', e)
      } finally {
        this.actionLoading = false
      }
    },
    async sendToPatientEmail() {
      const ids = this.getTargetIds()
      const email = this.patient.email
      const patientId = this.$route.params.id

      if (!ids.length) return

      if (ids.length > 10) {
        this.isErrorMessage = true
        this.alertIsOpen = true
        this.popupMessage = 'At most 10 documents can be sent in a single batch.'
        return
      }
      await this.TOGGLE_DIALOG({
        id: email,
        name: 'confirm',
        isOpen: true,
        title: 'Confirm Sending Email',
        description: `Send ${ids.length} document(s) to ${email}?`,
        callback: async () => {
          this.TOGGLE_DIALOG({name: 'confirm', isOpen: false})
          this.actionLoading = true

          try {
            const res = await this.sendDocumentsToPatient({patientId, documentIds: ids})
            if (res?.success) {
              this.isErrorMessage = false
              this.popupMessage = 'Documents sent successfully!'
            } else {
              this.isErrorMessage = true
              this.popupMessage = 'Failed to send email'
            }
            this.alertIsOpen = true
          } catch (e) {
            this.isErrorMessage = true
            this.alertIsOpen = true
            this.popupMessage = 'A server error occurred'
          } finally {
            this.actionLoading = false
          }
        }
      })
    },

    async handleDeleteFromModal() {
      this.TOGGLE_DIALOG({name: 'modalDocumentPreview', isOpen: false})
      await this.handleDelete()
    },
  },

  watch: {
    page() {
      const container = document.querySelector('.documents-list-container')
      if (container) container.scrollTop = 0
    },
    alertIsOpen(val) {
      const self = this
      if (val) {
        setTimeout(function () {
          self.alertIsOpen = false
        }, 5000)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.documents-page-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 100vh;


  //min-height: 500px;


  @media (max-width: 1234px) {
    max-height: none;
  }

  ::v-deep .v-input {
    .v-input--selection-controls__input {
      margin: 0;
      width: 20px;
      height: 20px;

      &:hover .v-input--selection-controls__ripple:before {
        opacity: 0;
      }

      .v-icon {
        color: #C9CED6;

        &.mdi-minus-box {
          color: #008C89 !important;
        }
      }

      .v-input--selection-controls__ripple {
        width: 20px;
        height: 20px;
        left: 0;
        top: 0;
        border-radius: 0;
        margin: 0;

        display: none;
      }
    }
  }

  .documents-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 2px 11px 4px;

    &__left {
      display: flex;
      align-items: center;
      gap: 10px;
      font-weight: 600;
      font-size: 15px;
      line-height: 20px;
      user-select: none;
    }

    &__count {
      color: #008C89;
    }

    &__title {
      color: #1F2D3D;
    }

    &__right {
      display: flex;
      align-items: center;
    }

    .button {
      ::v-deep .v-btn {
        background-color: #F7FAFC !important;
        border: 1px solid #CBD5E0 !important;
        color: #16192C;
        font-size: 12px;
        font-weight: 600;
        line-height: 12px;
        letter-spacing: 0;
        border-radius: 8px;
        padding: 10px 20px;

        &:focus::before {
          opacity: 0 !important;
        }

        .v-btn__content {
          column-gap: 10px;
        }
      }
    }
  }

  .documents-actions {
    display: flex;
    align-items: center;
    column-gap: 10px;
    margin-right: 13px;

    .icon-delete-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      cursor: pointer;

      .icon-delete {
        width: 16px;
        height: 16px;

        path {
          fill: #718096 !important;
        }
      }
    }

    .v-btn__content svg {
      width: 17px;
    }
  }

  .documents-list-container {
    flex: 0 0 auto;
    min-height: 82px;
    display: flex;
    flex-direction: column;
    height: 100%;


    @media (max-width: 1234px) {
      flex: 1;
    }

    .no-data-text {
      color: #A0AEC0;
      font-size: 18px;
      font-weight: 400;
      line-height: 30px;
    }
  }

  .documents-list {
    width: 100%;
    height: calc(100% - 64px);
    min-height: 500px;
    display: flex;
    flex-direction: column;
    row-gap: 1px;
    overflow-x: auto;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e0;
      border-radius: 10px;
      border: 2px solid #f1f5f9;
    }
  }

  .document-item {
    display: flex;
    column-gap: 10px;
    background-color: #FFFFFF;
    padding: 10px;
    cursor: pointer;

    &--selected,
    &--active {
      background-color: #EBF2FA;
    }

    &__main {
      display: flex;
      flex-direction: column;
      row-gap: 5px;
      min-width: 0;
      overflow: hidden;
    }

    &__name {
      color: #1A202C;
      font-size: 15px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__info {
      display: flex;
      align-items: center;
      column-gap: 9px;
      color: #718096;
      line-height: 20px;
      letter-spacing: 0;

      .badge {
        border-radius: 8px;
        background-color: #EBF2FA;
        font-size: 13px;
        font-weight: 500;
        padding: 3px 10px;
      }

      .source {
        font-size: 15px;
        font-weight: 400;
      }
    }

    &__meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      row-gap: 9px;
      margin-left: auto;
      min-width: max-content;
    }

    &__date {
      color: #2D3748;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0;
      white-space: nowrap;
      text-align: right;
    }
  }

  .loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgb(213 213 213 / 50%);
    z-index: 999;
  }

  .custom-alert {
    max-width: 300px;
    position: fixed;
    top: 10px;
    right: 20px;
    z-index: 100;
  }

  .custom-resizer {
    flex: 1;
    display: flex;
    max-width: 100%;

    > .pane {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    ::v-deep .multipane-resizer {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      position: relative;
      left: 0 !important;
      width: 24px;
      background-color: transparent;
      margin: 0 !important;
      cursor: col-resize;

      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        margin-top: -16px;
        height: 33px;
        width: 2px;
        background-color: #E2E8F0;
        border-radius: 8px 8px 0 0;
      }

      &:before {
        left: 6px;
      }

      &:after {
        right: 6px;
      }

      .center-line {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 2px;
        height: 177px;
        background-color: #E2E8F0;
        border-radius: 4px;
        margin-left: -1px;
        margin-top: -88px;
      }
    }
  }
}

.documents-manager {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.session-detail-preview {
  &__wrapper {
    display: flex;
    flex-direction: column;
    //flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;

    .detail-content {
      display: flex;
      flex-direction: column;
      //flex: 1 1 auto;
      min-height: 0;
      overflow: hidden;
    }
  }

  ::v-deep .table-wrapper .v-data-table {
    border-radius: 0;
  }

  .empty-placeholder-card {
    width: 100%;
    min-height: calc(100% - 64px);
    background-color: #EBF2FA;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    padding: 96px 10px 85px;
    overflow: hidden;

    .placeholder-table-header {
      display: flex;
      align-items: center;
      height: 32px;
      background-color: #FFFFFF;
      border-bottom: 1px solid #EDF2F7;

      div {
        color: #8492A6;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        text-align: center;
        letter-spacing: 0.6px;
      }
    }

    .placeholder-content {
      flex-grow: 1;
      background-color: #FAFAFA;
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        color: #A0AEC0;
        font-size: 18px;
        font-weight: 400;
        line-height: 30px;
      }
    }
  }
}

.detail-preview-bar {
  //flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 54px;
  background-color: #EBF2FA;
  border-radius: 8px 8px 0 0;
  padding: 10px 10px 10px 17px;
  margin-bottom: 2px;

  .bar-col-name {
    color: #16192C;
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .view-switcher {
    flex-shrink: 0;
    display: flex;
    background: #F7FAFC;
    border-radius: 8px;
    padding: 2px;

    &__btn {
      width: 88px;
      height: 30px;
      color: #425466;
      font-size: 12px;
      font-weight: 600;
      line-height: 12px;
      letter-spacing: 0;
      border: none;
      background: transparent;
      border-radius: 8px;
      padding: 9px 8px;
      cursor: pointer;
      transition: all 0.3s ease;

      &.is-active {
        background-color: #008C89;
        color: #FFFFFF;
      }
    }
  }
}

.drop-zone-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 140, 137, 0.08);
  border: 2px dashed #008C89;
  border-radius: 12px;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  .drop-zone-message {
    text-align: center;
    background: #fff;
    padding: 30px 60px;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

    svg {
      width: 48px;
      height: 48px;
      margin-bottom: 12px;
      color: #008C89;
    }

    p {
      font-weight: 600;
      color: #1A202C;
      font-size: 18px;
      margin: 0;
    }
  }
}

.pdf-viewer-container {
  position: relative;
  width: 100%;
  height: 590px;
  background-color: #edf2f7;
  overflow: hidden;

  .pdf-canvas-render {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;

    .pdf-render-surface {
      width: 100%;
      box-sizing: border-box;
      padding: 15px 15px 0;

      ::v-deep canvas {
        display: block;
        width: 100% !important;
        height: auto !important;
        max-width: 100% !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin: 0 auto 20px auto;
      }
    }

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e0;
      border-radius: 10px;
      border: 2px solid #f1f5f9;
    }
  }
}

.pagination-wrapper {
  padding: 10px 0;
  display: flex;
  justify-content: center;
}

::v-deep .custom-pagination {
  .v-pagination__item {
    border-radius: 8px;
    background-color: transparent;
    color: #718096 !important;
    box-shadow: unset !important;

    &--active {
      background-color: #E4ECF7 !important;
      border-radius: 8px !important;
      color: #425466 !important;
    }
  }

  .v-pagination__navigation {
    box-shadow: none;
    background-color: transparent !important;
  }
}

.hidden-input {
  display: none;
}
</style>
